import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/lib/adminReceivablesApi";
import type { ExpeditionPricingPreview } from "@/lib/ordersApi";

export interface ExpeditionConferenceValue {
  nfNumber: string;
  boletoConfirmed: boolean;
  boletoNote: string;
  /** Total exibido na prévia (informativo). Preenchido pelo modal. */
  expectedTotal?: number;
  /** Assinatura da composição conferida (prévia); obrigatória no backend. Preenchida pelo modal. */
  expectedSignature?: string;
}

export interface ExpeditionConditionInfo {
  paymentMethod?: PaymentMethod | null;
  paymentTermDays?: number | null;
  installments?: number | null;
}

/**
 * Conferência manual obrigatória na conclusão da expedição (Etapa 2). Captura a NF emitida
 * externamente e, quando a condição é BOLETO, exige a confirmação de que o boleto foi gerado fora do
 * sistema conforme a condição do cliente. O backend é a fonte da verdade e revalida tudo.
 */
export function ExpeditionConferenceFields({
  value,
  onChange,
  condition,
  pricing,
}: {
  value: ExpeditionConferenceValue;
  onChange: (next: ExpeditionConferenceValue) => void;
  condition?: ExpeditionConditionInfo | null;
  pricing?: ExpeditionPricingPreview | null;
}) {
  // Quando a condição é conhecida, só exige boleto para BOLETO. Se desconhecida, mostra a confirmação
  // (opcional) — o backend recusa se realmente for necessária.
  const boletoRequired = condition ? condition.paymentMethod === "BOLETO" : true;
  // Condição carregada, mas incompleta (sem forma ou sem prazo): a conclusão da expedição será
  // bloqueada pelo backend. Avisar aqui para o operador corrigir antes de tentar.
  const conditionIncomplete = condition != null
    && (condition.paymentMethod == null || condition.paymentTermDays == null);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-background/70 p-3">
      <div>
        <p className="text-sm font-medium">Conferência financeira</p>
        {condition && condition.paymentMethod && condition.paymentTermDays != null ? (
          <p className="text-xs text-muted-foreground">
            Condição do cliente: {PAYMENT_METHOD_LABELS[condition.paymentMethod]}
            {` · ${condition.paymentTermDays} dias`}
            {condition.installments != null ? ` · ${condition.installments}x` : ""}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Informe a NF emitida externamente (opcional nesta etapa).
          </p>
        )}
        {conditionIncomplete && (
          <p className="mt-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-700">
            Cliente sem condição financeira configurada (forma de pagamento e prazo). Configure em
            Clientes → Condição financeira padrão antes de concluir a expedição.
          </p>
        )}
        {pricing && pricing.allPriced && pricing.total && (
          <p className="mt-1 text-xs text-muted-foreground">
            Total do pedido: <strong className="text-foreground">{pricing.total}</strong>
          </p>
        )}
        {pricing && !pricing.allPriced && (
          <div className="mt-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-700">
            Itens sem preço resolvível — cadastre o preço na tabela do cliente antes de concluir:
            <ul className="mt-0.5 list-inside list-disc">
              {pricing.unpriced.map((u) => (
                <li key={u}>{u}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="conf-nf">Número da NF</Label>
        <Input
          id="conf-nf"
          value={value.nfNumber}
          onChange={(e) => onChange({ ...value, nfNumber: e.target.value })}
          placeholder="NF emitida no sistema externo (opcional)"
        />
        <p className="text-xs text-muted-foreground">
          Opcional nesta etapa. Se não informada agora, poderá ser preenchida na confirmação de entrega.
        </p>
      </div>

      {boletoRequired && (
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id="conf-boleto"
              checked={value.boletoConfirmed}
              onCheckedChange={(checked) => onChange({ ...value, boletoConfirmed: checked === true })}
              className="mt-0.5"
            />
            <Label htmlFor="conf-boleto" className="cursor-pointer text-sm font-normal">
              Confirmo que o boleto foi gerado externamente conforme a condição do cliente.
            </Label>
          </div>
          <Textarea
            value={value.boletoNote}
            onChange={(e) => onChange({ ...value, boletoNote: e.target.value })}
            placeholder="Observação do boleto (opcional)"
            rows={2}
          />
        </div>
      )}
    </div>
  );
}
